import { useEffect, useRef, type MouseEvent, type PointerEvent } from 'react';

/** A press longer than this is a hold: releasing the button ends the recording. */
export const HOLD_MS = 350;

/**
 * Hold or tap on one button (user, 2026-09-24, as in Ubu): pointer down starts the recording;
 * releasing after HOLD_MS stops it (push-to-talk), a shorter tap keeps it running until the
 * next tap or the automatic end. The release is watched on the window, so it also counts when
 * the button re-renders meanwhile. A click that follows a pointer press is ignored; the
 * keyboard (Enter/Space) still toggles through `onClick`.
 */
export function useHoldOrTap({
  active,
  start,
  stop,
}: {
  active: boolean;
  start: () => void;
  stop: () => void;
}) {
  const downAt = useRef(0);
  const holding = useRef(false);
  const pointerHandled = useRef(false);
  const latest = useRef({ active, start, stop });
  latest.current = { active, start, stop };
  const release = useRef<() => void>(() => undefined);
  useEffect(() => () => release.current(), []);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 || e.currentTarget.disabled) return;
    pointerHandled.current = true;
    // A press without its click (a cancelled touch) must not swallow a later keyboard click.
    window.setTimeout(() => (pointerHandled.current = false), 700);
    if (latest.current.active) {
      latest.current.stop();
      return;
    }
    downAt.current = Date.now();
    holding.current = true;
    const up = () => {
      release.current();
      if (Date.now() - downAt.current >= HOLD_MS) latest.current.stop();
    };
    release.current = () => {
      holding.current = false;
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      release.current = () => undefined;
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    latest.current.start();
  };
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (pointerHandled.current) {
      pointerHandled.current = false;
      return;
    }
    if (latest.current.active) latest.current.stop();
    else latest.current.start();
    void e;
  };
  return {
    /** True while the button is held down – silence must not end the recording then. */
    held: () => holding.current,
    handlers: {
      onPointerDown,
      onClick,
      onContextMenu: (e: MouseEvent) => e.preventDefault(),
    },
  };
}
